import AmpRealtime from '@amplify/amp-realtime';
import { isNil } from 'ramda';

import { getAmpConfig } from 'shared/config/amp-config';
import { AuthService, UserRoles } from 'shared/lib/keycloak';
import { loggerService } from 'shared/lib/logger';
import {
  getAssignmentContentLessonKeys,
  isDesmosAssignment,
} from 'shared/lib/utils';
import { Assignment } from 'shared/api/assignment-service';

import { ACTIVITY_ASSIGNED_EVENT, ASSIGNMENT_UPDATED_EVENT } from './constants';
import { RealtimeCallback } from './types';

class Realtime {
  public ampRealtime: AmpRealtime | null = null;
  private _channelNames: string[] = [];
  private _listeners: Record<string, RealtimeCallback> = {};

  init() {
    const config = getAmpConfig();
    const userBk = AuthService.getUserBk();

    this.ampRealtime = new AmpRealtime(config, userBk, {
      filterConfig: { userRole: UserRoles.TEACHER },
    });
  }

  generateRealtimeEventPayload({
    assignment,
    startDate,
    dueDate,
    isDeleted,
    newActivitiesCount,
  }: {
    assignment: Assignment;
    startDate: number | null;
    dueDate: number | null;
    isDeleted?: boolean;
    newActivitiesCount?: number;
  }) {
    if (isDesmosAssignment(assignment)) {
      return {
        ampAssignmentKey: assignment.businessKey,
        // Desmos Classroom will update assignment in realtime only if sender is 'amplify'
        sender: 'amplify',
        isDeleted,
        startDate: startDate ?? undefined,
        dueDate: dueDate ?? undefined,
        classBk: assignment.classBks[0],
        assignedContent: [
          {
            type: assignment.type,
            contentBusinessKey: assignment.content.contentBusinessKey,
            content: assignment.content,
          },
        ],
        isFromEducatorHome: true,
      };
    }

    const assignmentContentLessonKeys =
      getAssignmentContentLessonKeys(assignment);

    return {
      assignedContent: [
        {
          activitiesBks: assignmentContentLessonKeys,
          classBk: assignment.classBks[0],
        },
      ],
      isDeleted,
      isLessonLevelAssignment: true,
      newActivitiesCount,
      startDate,
      isFromEducatorHome: true,
    };
  }

  sendRealtimeEvent(
    channelName: string,
    eventType: string,
    {
      assignment,
      startDate,
      dueDate,
      isDeleted,
      newActivitiesCount,
    }: {
      assignment: Assignment;
      startDate: number | null;
      dueDate: number | null;
      isDeleted?: boolean;
      newActivitiesCount?: number;
    },
  ): Promise<void> {
    if (isNil(this.ampRealtime)) {
      loggerService.logError('Realtime service is not initiated');
      return Promise.resolve();
    }

    const realtimeEventPayload = this.generateRealtimeEventPayload({
      assignment,
      startDate,
      dueDate,
      isDeleted,
      newActivitiesCount,
    });

    return this.ampRealtime.publish(
      channelName,
      eventType,
      realtimeEventPayload,
    );
  }

  subscribeToAssignments(
    newAssignmentCallback: RealtimeCallback,
    updateAssignmentCallback: RealtimeCallback,
    userClassesKeys: string[],
  ) {
    this._channelNames = userClassesKeys.map((classKey) => `${classKey}`);

    this.ampRealtime?.subscribeToChannels(this._channelNames, false);

    this._listeners = {
      [ACTIVITY_ASSIGNED_EVENT]: newAssignmentCallback,
      [ASSIGNMENT_UPDATED_EVENT]: updateAssignmentCallback,
    };

    Object.entries(this._listeners).forEach(([event, callback]) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.ampRealtime?.addEventListener(event, callback);
    });
  }

  unsubscribeFromAssignments() {
    Object.entries(this._listeners).forEach(([event, callback]) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.ampRealtime?.removeEventListener(event, callback);
    });

    this.ampRealtime?.unsubscribeFromChannels(this._channelNames);
  }
}

const realtimeService = new Realtime();

export default realtimeService;
